import React from 'react';

/**
 * EmailCategoryBadge - Displays category badge for emails
 * Supports both predefined categories and custom user-created categories
 * Size options: 'sm' (compact), 'md' (default)
 */
const EmailCategoryBadge = ({ category, size = 'md' }) => {
  if (!category || category === 'all') return null;

  const badgeConfig = {
    urgent: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'URGENT' },
    orders: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'ORDERS' },
    meeting: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'MEETING' },
    client: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'CLIENT' },
    report: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'REPORT' },
    travel: { bg: 'bg-orange-500/20', text: 'text-orange-400', label: 'TRAVEL' },
    other: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'OTHER' },
  };

  // Check if it's a known category
  let config = badgeConfig[category.toLowerCase()];
  
  // For custom categories, generate a consistent color based on category name
  if (!config) {
    const customColors = [
      { bg: 'bg-pink-500/20', text: 'text-pink-400' },
      { bg: 'bg-indigo-500/20', text: 'text-indigo-400' },
      { bg: 'bg-teal-500/20', text: 'text-teal-400' },
      { bg: 'bg-rose-500/20', text: 'text-rose-400' },
      { bg: 'bg-emerald-500/20', text: 'text-emerald-400' },
      { bg: 'bg-amber-500/20', text: 'text-amber-400' },
      { bg: 'bg-violet-500/20', text: 'text-violet-400' },
      { bg: 'bg-lime-500/20', text: 'text-lime-400' },
      { bg: 'bg-cyan-500/20', text: 'text-cyan-400' },
      { bg: 'bg-fuchsia-500/20', text: 'text-fuchsia-400' },
    ];
    
    // Use hash of category name to pick a consistent color
    let hash = 0;
    for (let i = 0; i < category.length; i++) {
      hash = ((hash << 5) - hash) + category.charCodeAt(i);
      hash = hash & hash;
    }
    const colorIndex = Math.abs(hash) % customColors.length;
    const color = customColors[colorIndex];
    
    // Format label (capitalize words, replace underscores/hyphens with spaces)
    const label = category
      .replace(/[-_]/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .toUpperCase();
    
    config = { ...color, label };
  }

  const sizeClasses = size === 'sm' 
    ? 'px-1.5 py-0.5 text-[10px]' 
    : 'px-2 py-0.5 text-xs';

  return (
    <span 
      className={`${sizeClasses} ${config.bg} ${config.text} font-semibold rounded`}
      data-testid={`email-badge-${category}`}
    >
      {config.label}
    </span>
  );
};

export default EmailCategoryBadge;
